import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Camera, Check, MapPin, LogOut, Truck, X, User as UserIcon, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function Driver() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(null);
  const [current, setCurrent] = useState(0);
  const [posting, setPosting] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const watchIdRef = useRef(null);

  const loadTrip = async () => {
    try {
      const r = await api.get("/driver/my-trip");
      setTrip(r.data);
      setError(null);
      // Auto-jump to first incomplete stop
      const idx = (r.data.stops || []).findIndex((s) => !s.completed);
      setCurrent(idx === -1 ? (r.data.stops?.length || 1) - 1 : idx);
    } catch (e) {
      setError(e?.response?.data?.detail || "No trip assigned");
    }
  };

  useEffect(() => { loadTrip(); }, []);
  // Force password change on first login (admin created account with a temp password)
  useEffect(() => { if (user?.must_change_password) setShowProfile(true); }, [user?.must_change_password]);

  // Start GPS streaming while the driver is on the road
  useEffect(() => {
    if (!navigator.geolocation) return;
    const post = (pos) => {
      api.post("/driver/location", {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        heading: pos.coords.heading,
        speed: pos.coords.speed,
        trip_id: trip?.id || null,
      }).catch(() => {});
    };
    const watchId = navigator.geolocation.watchPosition(post, () => {}, {
      enableHighAccuracy: true, maximumAge: 20_000, timeout: 20_000,
    });
    watchIdRef.current = watchId;
    const beat = setInterval(() => {
      navigator.geolocation.getCurrentPosition(post, () => {}, { maximumAge: 30_000, timeout: 20_000 });
    }, 30_000);
    return () => {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
      clearInterval(beat);
    };
  }, [trip?.id]);

  const patchStop = async (payload) => {
    setPosting(true);
    try {
      const { data } = await api.patch(`/driver/my-trip/stops/${current}`, payload);
      setTrip(data);
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
      return null;
    } finally { setPosting(false); }
  };

  const onPhoto = async (kind, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Photo must be under 2MB"); return; }
    const dataUrl = await fileToDataUrl(file);
    const payload = kind === "before" ? { photo_before: dataUrl } : { photo_after: dataUrl };
    await patchStop(payload);
    toast.success(`${kind === "before" ? "Before" : "After"} photo saved`);
  };

  const removePhoto = async (kind) => {
    const payload = kind === "before" ? { photo_before: "" } : { photo_after: "" };
    await patchStop(payload);
  };

  const markArrived = () => patchStop({ arrived: true });
  const completeStop = async () => {
    const stop = trip.stops[current];
    if (!stop.photo_before || !stop.photo_after) { toast.error("Take both before and after photos first."); return; }
    const updated = await patchStop({ completed: true });
    if (!updated) return;
    toast.success("Stop completed");
    const next = (updated.stops || []).findIndex((s) => !s.completed);
    if (next !== -1) setCurrent(next);
    else toast.success("All stops done — head back to depot 🙌");
  };

  const handleLogout = async () => { await logout(); nav("/login"); };

  const stop = trip?.stops?.[current];
  const totalStops = trip?.stops?.length || 0;
  const completedCount = (trip?.stops || []).filter((s) => s.completed).length;
  const progress = totalStops ? Math.round((completedCount / totalStops) * 100) : 0;
  const mapsUrl = (s) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s?.address || s?.site_name || "")}`;

  return (
    <div className="min-h-screen" style={{ background: "var(--bone)" }}>
      <header className="glass-header sticky top-0 z-30">
        <div className="px-5 py-3 flex items-center justify-between">
          <Logo size={36} />
          <div className="flex items-center gap-3">
            <button onClick={() => setShowProfile(true)} className="text-xs text-neutral-500 flex items-center gap-1" data-testid="driver-profile-btn"><UserIcon className="w-4 h-4" /> {user?.name?.split(" ")[0]}</button>
            <button onClick={handleLogout} className="text-xs text-neutral-500 flex items-center gap-1" data-testid="driver-logout"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </header>

      <div className="p-5 pb-24 max-w-[520px] mx-auto space-y-4">
        {error && (
          <div className="hbt-card p-8 text-center" data-testid="no-trip-empty">
            <Truck className="w-8 h-8 mx-auto mb-3 opacity-40" />
            <div className="font-display text-xl font-semibold mb-1">No trip today</div>
            <div className="text-sm text-neutral-500">{error}</div>
            <button onClick={loadTrip} className="btn-outline text-xs mt-4">Refresh</button>
          </div>
        )}

        {trip && (
          <>
            <div className="hbt-card p-5" data-testid="trip-header">
              <div className="hbt-label">Trip · {trip.trip_number}</div>
              <div className="mt-2 font-display text-lg font-semibold">{user.name} · {trip.vehicle}</div>
              <div className="text-xs text-neutral-500 font-mono-num mt-1">{trip.trip_date} · {trip.start_time || "—"}{trip.end_time ? `–${trip.end_time}` : ""}</div>
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span>Progress</span><span className="font-mono-num">{completedCount} / {totalStops}</span>
                </div>
                <div className="h-2 rounded-full bg-neutral-100 overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${progress}%`, background: "var(--forest)" }} />
                </div>
              </div>
              {/* Stop dots for quick nav */}
              {totalStops > 0 && (
                <div className="flex gap-1.5 mt-3">
                  {trip.stops.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrent(i)}
                      className="flex-1 h-1.5 rounded-full transition-colors"
                      style={{ background: s.completed ? "var(--forest)" : i === current ? "#C85A3C" : "rgba(10,10,10,0.12)" }}
                      data-testid={`stop-dot-${i}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {stop && (
              <div className="hbt-card p-5 space-y-4" data-testid="current-stop-card">
                <div>
                  <div className="hbt-label">Stop {current + 1} of {totalStops}</div>
                  <div className="font-display text-2xl font-semibold mt-1 leading-tight">{stop.site_name || "Site"}</div>
                  <div className="text-sm text-neutral-500 mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {stop.address || "—"}</div>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {stop.waste_type && <span className="pill forest">{stop.waste_type}</span>}
                    {stop.completed && <span className="pill success inline-flex items-center gap-1"><Check className="w-3 h-3" /> Completed</span>}
                    {!stop.completed && stop.arrived_at && <span className="pill">On site</span>}
                  </div>
                  {(stop.address || stop.site_name) && (
                    <a href={mapsUrl(stop)} target="_blank" rel="noreferrer" className="btn-outline text-xs mt-3 inline-flex items-center gap-1.5" data-testid="open-maps">
                      <MapPin className="w-3.5 h-3.5" /> Open in Maps
                    </a>
                  )}
                </div>

                {!stop.arrived_at && !stop.completed && (
                  <button onClick={markArrived} disabled={posting} className="btn-outline w-full py-3 inline-flex items-center justify-center gap-2" data-testid="arrived-btn">
                    <MapPin className="w-4 h-4" /> I&apos;ve arrived
                  </button>
                )}

                {["before","after"].map((kind) => {
                  const key = kind === "before" ? "photo_before" : "photo_after";
                  const val = stop[key];
                  return (
                    <div key={kind}>
                      <label className="hbt-label block mb-2">{kind === "before" ? "1 · Before photo" : "2 · After photo"}</label>
                      {val ? (
                        <div className="relative rounded overflow-hidden border" style={{ borderColor: "var(--border)" }}>
                          <img src={val} alt="" className="w-full h-40 object-cover" />
                          <button onClick={() => removePhoto(kind)} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <label className="btn-outline w-full flex items-center justify-center gap-2 py-4 cursor-pointer" data-testid={`take-${kind}-photo`}>
                          <Camera className="w-5 h-5" /> Take {kind} photo
                          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onPhoto(kind, e)} />
                        </label>
                      )}
                    </div>
                  );
                })}

                {!stop.completed && (
                  <button
                    onClick={completeStop}
                    disabled={!stop.photo_before || !stop.photo_after || posting}
                    className="btn-primary w-full py-4 flex items-center justify-center gap-2"
                    data-testid="complete-stop"
                  >
                    <Check className="w-5 h-5" /> {posting ? "Saving…" : "Complete stop"}
                  </button>
                )}
              </div>
            )}

            {totalStops === 0 && (
              <div className="hbt-card p-8 text-center">
                <div className="font-display text-lg font-semibold">Trip has no stops yet</div>
                <div className="text-sm text-neutral-500 mt-1">Ask the office to add stops before you head out.</div>
              </div>
            )}
          </>
        )}
      </div>

      {showProfile && <DriverProfile onClose={() => setShowProfile(false)} />}
    </div>
  );
}

function DriverProfile({ onClose }) {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [saving, setSaving] = useState(false);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/me", { name, phone });
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const changePw = async () => {
    if (newPw.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    setSaving(true);
    try {
      await api.post("/auth/me/password", { current_password: currentPw, new_password: newPw });
      toast.success("Password changed — please sign in again on your other devices.");
      setCurrentPw(""); setNewPw("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="hbt-card p-6 w-full sm:max-w-md rounded-t-2xl sm:rounded" onClick={(e) => e.stopPropagation()} data-testid="driver-profile-modal">
        <div className="flex items-center justify-between mb-5">
          <div className="font-display text-xl font-semibold">Your profile</div>
          <button onClick={onClose}><X className="w-5 h-5 text-neutral-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="hbt-label block mb-2">Full name</label>
            <input className="hbt-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" />
          </div>
          <div>
            <label className="hbt-label block mb-2">Phone</label>
            <input className="hbt-input" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="profile-phone-input" />
          </div>
          <button onClick={saveProfile} disabled={saving} className="btn-primary w-full" data-testid="profile-save-btn">Save profile</button>
        </div>
        <div className="mt-6 pt-5 border-t" style={{ borderColor: "var(--border)" }}>
          <div className="font-display font-semibold flex items-center gap-2 mb-3"><KeyRound className="w-4 h-4" /> Change password</div>
          <div className="space-y-3">
            <input type="password" placeholder="Current password" className="hbt-input" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} data-testid="profile-currpw" />
            <input type="password" placeholder="New password (8+ chars)" className="hbt-input" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="profile-newpw" />
            <button onClick={changePw} disabled={saving || !currentPw || !newPw} className="btn-outline w-full" data-testid="profile-change-pw">Change password</button>
          </div>
        </div>
      </div>
    </div>
  );
}

